import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useMobileWallet } from "../wallet/MobileWalletProvider";
import InvitationsExperience from "../../magic-link/InvitationsExperience";
import { getMobileUserProfile } from "../api/storageApi";
// Dedicated mobile page for guest invitations and the meeting calendar.
// Extracted from MobileSettingsPage (mirrors the desktop /invitations page)
// as the invite tooling grew and Settings became too crowded.
const MobileInvitationsPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { address, isAuthenticated } = useMobileWallet();
    const returnToSecureChat = location.state?.returnToSecureChat === true;
    const returnToMobileAppChat = new URLSearchParams(location.search).get("returnToMobileAppChat") === "1";
    // The legacy secure-chat calendar entry carried only its return marker.
    // Explicit tab state wins; this is initial state, never a forced tab lock.
    const initialKind = location.state?.invitationTab === "scheduled" ||
        (!location.state?.invitationTab && returnToSecureChat) ? "scheduled" : undefined;
    // EIP-1193 provider from the mobile AppKit session — enables the invite
    // panel's template dropdown (wallet-storage template read/decrypt).
    const [walletProvider, setWalletProvider] = useState(null);
    // Organizer's profile e-mail: session-mirrored copy first (instant), then
    // the wallet-encrypted profile as fallback. Used for the calendar-sync mail.
    const [hostEmail, setHostEmail] = useState(() => {
        try {
            const raw = sessionStorage.getItem("dw_mobile_profile_email");
            return raw ? JSON.parse(raw) || null : null;
        }
        catch {
            return null;
        }
    });
    useEffect(() => {
        if (!isAuthenticated || hostEmail)
            return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getMobileUserProfile();
                if (!cancelled && res?.data?.email)
                    setHostEmail(res.data.email);
            }
            catch {
                /* no profile e-mail — organizer mail simply not sent */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, hostEmail]);
    useEffect(() => {
        if (!isAuthenticated) {
            setWalletProvider(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { getAppKitSync, initAppKit } = await import("../wallet/mobileWalletConfig");
                let modal = getAppKitSync();
                if (!modal)
                    modal = await initAppKit();
                if (!modal || cancelled)
                    return;
                for (let attempt = 0; attempt < 5; attempt++) {
                    if (cancelled)
                        return;
                    const prov = modal.getWalletProvider?.();
                    if (prov) {
                        setWalletProvider(prov);
                        return;
                    }
                    await new Promise((r) => setTimeout(r, 1000));
                }
            }
            catch (err) {
                console.warn("[MobileInvitations] Wallet provider unavailable:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);
    return _jsx(InvitationsExperience, { mobile: true, initialKind: initialKind, hostWallet: address, walletProvider: walletProvider, hostEmail: hostEmail, mobileChatHref: returnToMobileAppChat ? "/m/?openChat=1" : undefined, onBack: returnToSecureChat && !returnToMobileAppChat ? () => navigate("/chat", { replace: true }) : undefined, backLabel: t("magicLink.navigation.backToSecureChat") });
};
export default MobileInvitationsPage;
